import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useMessages } from "@/lib/hooks/use-api";
import { useMessagesContext } from "@/lib/contexts/MessagesContext";

interface UsePreloadMessagesOptions {
  inboxId: string;
  provider: string;
  enabled?: boolean;
}

export function usePreloadMessages({
  inboxId,
  provider,
  enabled = true,
}: UsePreloadMessagesOptions) {
  const { getMessages, setMessages, setLoading, setError } = useMessagesContext();

  // Map old provider keys to new the provider provider keys
  const providerMap: Record<string, string> = {
    gmail: "google-mail",
    outlook: "outlook-oauth",
    slack: "slack-oauth",
  };

  const providerKey = providerMap[provider] || provider;

  // Check if we already have messages for this inbox/provider
  const existingMessages = inboxId && providerKey ? getMessages(inboxId, providerKey) : [];

  // Preload messages if we don't have them
  const { data, isLoading, error } = useMessages(
    providerKey,
    {
      connectionId: inboxId,
      limit: 25,
      skip: 0,
      filter: "all",
    },
    Boolean(enabled && existingMessages.length === 0 && inboxId && providerKey)
  );

  // Update context when data is loaded
  useEffect(() => {
    if (data && inboxId && providerKey) {
      setMessages(inboxId, providerKey, data.messages, data.hasMore, data.nextPageToken);
      setLoading(inboxId, providerKey, false);
      setError(inboxId, providerKey, null);
    }
  }, [data, inboxId, providerKey, setMessages, setLoading, setError]);

  // Update loading state
  useEffect(() => {
    if (enabled && existingMessages.length === 0 && inboxId && providerKey) {
      setLoading(inboxId, providerKey, isLoading);
    }
  }, [isLoading, enabled, existingMessages.length, inboxId, providerKey, setLoading]);

  // Update error state
  useEffect(() => {
    if (error && inboxId && providerKey) {
      setError(inboxId, providerKey, error.message);
    }
  }, [error, inboxId, providerKey, setError]);

  return {
    isLoading,
    error: error?.message || null,
    hasData: existingMessages.length > 0 || !!data,
  };
}
